import { Select } from '@mantine/core';
import { useDebouncedValue, useInputState } from '@mantine/hooks';
import { Icon } from '@repo/foundations';
import { useCallback } from 'react';
import type { SearchResult } from '../../hooks/useSearch';
import useSearch from '../../hooks/useSearch';
import { EntityIcon } from '../EntityIcon/EntityIcon';
import TemporaryNodeEntitySelectItem from './TemporaryNodeEntitySelectItem';

interface TemporaryNodeEntitySelectorProps {
	selectedEntity: SearchResult | null;
	setSelectedEntity: (entity: SearchResult | null) => void;
}

function TemporaryNodeEntitySelect({
	selectedEntity,
	setSelectedEntity,
}: TemporaryNodeEntitySelectorProps) {
	const [searchTerm, setSearchTerm] = useInputState('');
	const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300);

	const { data: searchResults, isLoading } = useSearch({
		searchTerm: debouncedSearchTerm,
	});

	const handleOnChange = useCallback(
		(id: string) => {
			const selected = searchResults?.find((result) => result.id === id);
			if (selected) {
				setSelectedEntity(selected);
			}
		},
		[searchResults, setSelectedEntity]
	);

	return (
		<Select
			classNames={{
				itemsWrapper: 'nowheel nopan nodrag',
			}}
			label="Add resource to lineage"
			placeholder="Search resources "
			searchable
			filter={() => true}
			searchValue={searchTerm}
			onSearchChange={setSearchTerm}
			onChange={handleOnChange}
			data={
				searchResults?.map((result) => ({
					label: result.title,
					value: result.id,
					entity: result,
				})) ?? []
			}
			value={selectedEntity?.id}
			icon={
				selectedEntity ? (
					<EntityIcon
						entityType={selectedEntity.entity_type}
						integrationType={selectedEntity.integration_type ?? undefined}
						size={20}
					/>
				) : (
					<Icon name="search" color="icon/primary/default" />
				)
			}
			rightSection={<Icon name="selector" color="icon/primary/default" />}
			dropdownPosition="bottom"
			itemComponent={TemporaryNodeEntitySelectItem}
		/>
	);
}

export default TemporaryNodeEntitySelect;
